// Complete Profile Page - For Google Auth users who need to add company details
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { n8nApi } from '../../services/n8nApi'

const CompleteProfilePage = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuth()

  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    phone: ''
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingExisting, setIsCheckingExisting] = useState(true)

  // Check if user already has an Airtable record (prevents duplicates)
  useEffect(() => {
    const checkExistingRecord = async () => {
      if (!user?.uid) {
        setIsCheckingExisting(false)
        return
      }

      try {
        const response = await n8nApi.getUserStatus(user.uid)
        if (response.found && response.prospect) {
          // User already has a record - redirect to onboarding
          console.log('✅ User already has Airtable record, redirecting to onboarding')
          navigate('/onboarding', { replace: true })
          return
        }
      } catch (err) {
        // If check fails, allow them to proceed (better UX than blocking)
        console.warn('Could not check for existing record:', err)
      }

      setIsCheckingExisting(false)
    }

    if (isAuthenticated && user?.uid) {
      checkExistingRecord()
    }
  }, [user?.uid, isAuthenticated, navigate])

  // Pre-fill name from Google profile
  useEffect(() => {
    if (user?.displayName) {
      setFormData(prev => ({ ...prev, fullName: user.displayName }))
    }
  }, [user])

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/signup')
    }
  }, [isAuthenticated, navigate])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validate = () => {
    const newErrors = {}
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required'
    }
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) return

    setIsLoading(true)
    setErrors({})

    try {
      // Double-check for existing record before creating (prevents duplicates on retry/back navigation)
      try {
        const existingCheck = await n8nApi.getUserStatus(user.uid)
        if (existingCheck.found && existingCheck.prospect) {
          console.log('✅ Record already exists, redirecting...')
          navigate('/onboarding', { replace: true })
          return
        }
      } catch (checkErr) {
        // If check fails, proceed with creation (will fail if duplicate anyway)
        console.warn('Pre-submit check failed, proceeding:', checkErr)
      }

      console.log('📤 Submitting profile data...')
      const response = await n8nApi.createUserWithDetails(user.uid, {
        email: user.email,
        full_name: formData.fullName,
        company_name: formData.companyName,
        phone: formData.phone || ''
      })

      console.log('📥 Response:', response)

      // Success if we got an array with a record, or an object with an id
      if (response && (Array.isArray(response) ? response[0]?.id : response.id || response.success)) {
        console.log('✅ Record created, navigating...')
        // Pass state to signal OnboardingContext that we just created a record
        navigate('/onboarding/schedule-intro', { replace: true, state: { justCreatedRecord: true } })
      } else {
        throw new Error('Failed to create user record')
      }

    } catch (err) {
      console.error('Profile completion error:', err)
      setErrors({ submit: 'Failed to save your information. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }

  // Show loading while checking for existing record
  if (isCheckingExisting) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex flex-col items-center gap-3">
          <img
            src="/assets/INSUREVISION_AI_LOGO_WH.svg"
            alt="InsureVision"
            className="h-20"
          />
          <span className="font-display font-bold text-2xl text-slate-900">
            FleetVision
          </span>
        </div>
        <h2 className="mt-6 text-center text-3xl font-display font-bold text-brand-950">
          Complete Your Profile
        </h2>
        <p className="mt-2 text-center text-sm text-slate-600">
          Just a few more details to get you started
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-soft rounded-2xl sm:px-10 border border-slate-200">
          
          {/* Account info */}
          <div className="mb-6 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">{user?.email}</p>
                <p className="text-xs text-slate-500">Signed in</p>
              </div>
            </div>
          </div>

          {errors.submit && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-700">{errors.submit}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className="label">
                Full Name <span className="text-accent-600">*</span>
              </label>
              <input
                id="fullName"
                name="fullName"
                type="text"
                required
                value={formData.fullName}
                onChange={handleChange}
                className={`input-field ${errors.fullName ? 'border-red-300' : ''}`}
                placeholder="Jane Smith"
              />
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
              )}
            </div>

            {/* Company Name */}
            <div>
              <label htmlFor="companyName" className="label">
                Company Name <span className="text-accent-600">*</span>
              </label>
              <input
                id="companyName"
                name="companyName"
                type="text"
                required
                value={formData.companyName}
                onChange={handleChange}
                className={`input-field ${errors.companyName ? 'border-red-300' : ''}`}
                placeholder="Acme Transport Ltd"
              />
              {errors.companyName && (
                <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="label">
                Phone Number <span className="text-slate-400">(optional)</span>
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="input-field"
                placeholder="+44 7700 900000"
              />
            </div>

            {/* What happens next */}
            <div className="bg-brand-50 rounded-xl p-4 border border-brand-100">
              <h4 className="font-semibold text-brand-950 text-sm mb-2">What happens next?</h4>
              <p className="text-sm text-slate-600">
                After completing your profile, you'll book a 30-minute intro call with Robert Putt 
                to discuss your fleet's risk profile and how FleetVision can help.
              </p>
            </div>

            {/* Submit */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  'Continue to Book Intro Call'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CompleteProfilePage
